/**
 * Static navigation data for UKSS theme.
 * All nav items, footer sections, and payment icons are hardcoded
 * to match the approved UKSS storefront design.
 *
 * TODO: Most hrefs point to /store (or # for customer service pages) as
 * placeholders. Update when Medusa collections/categories and CMS pages
 * are configured.
 */

// ─── Types ──────────────────────────────────────────────────────────────────

export interface NavItem {
  label: string
  href: string
  /** If true, the item is styled as highlighted (e.g. yellow text for Sale) */
  highlight?: boolean
}

export interface FooterLink {
  label: string
  href: string
  external?: boolean
}

export interface FooterSection {
  title: string
  links: FooterLink[]
}

export interface PaymentIcon {
  id: string
  name: string
  image: string
}

export type TrustFeatureIcon = "truck" | "shield" | "edit" | "grid"
export type SocialIcon = "facebook" | "twitter"

// ─── Primary Navigation ─────────────────────────────────────────────────────

export const PRIMARY_NAV_ITEMS: NavItem[] = [
  { label: "Home", href: "/" },
  { label: "Players", href: "/store" },
  { label: "Football Shirts", href: "/store" },
  { label: "Club Teams", href: "/store" },
  { label: "International", href: "/store" },
  { label: "Clothing", href: "/store" },
  { label: "Gifts", href: "/store" },
  { label: "Kids Sizes", href: "/store" },
  { label: "T-Shirts", href: "/store" },
  { label: "Rugby", href: "/store" },
  { label: "New Arrivals", href: "/store" },
  { label: "Specials", href: "/store", highlight: true },
  { label: "Wildcard", href: "/store" },
]

// ─── Header Trust Features ──────────────────────────────────────────────────

export const HEADER_TRUST_FEATURES = [
  { icon: "truck" as const, title: "Next Day Delivery", subtitle: "Worldwide shipping available" },
  { icon: "shield" as const, title: "100% Official", subtitle: "Genuine football shirts & kits" },
  { icon: "edit" as const, title: "Personalisation", subtitle: "Any name & number added" },
  { icon: "grid" as const, title: "Huge Range", subtitle: "Kits from 500+ teams" },
]

// ─── Footer Sections ────────────────────────────────────────────────────────

export const FOOTER_SECTIONS: FooterSection[] = [
  {
    title: "Top Teams",
    links: [
      { label: "AC Milan Shirts", href: "/store" },
      { label: "Arsenal Shirts", href: "/store" },
      { label: "Barcelona Shirts", href: "/store" },
      { label: "Chelsea Shirts", href: "/store" },
      { label: "Inter Milan Shirts", href: "/store" },
      { label: "Juventus Shirts", href: "/store" },
      { label: "Liverpool Shirts", href: "/store" },
      { label: "Man Utd Shirts", href: "/store" },
      { label: "Tottenham Shirts", href: "/store" },
    ],
  },
  {
    title: "National Teams",
    links: [
      { label: "Argentina Shirts", href: "/store" },
      { label: "Brazil Shirts", href: "/store" },
      { label: "England Shirts", href: "/store" },
      { label: "France Shirts", href: "/store" },
      { label: "Germany Shirts", href: "/store" },
      { label: "Holland Shirts", href: "/store" },
      { label: "Portugal Shirts", href: "/store" },
      { label: "Scotland Shirts", href: "/store" },
      { label: "Spain Shirts", href: "/store" },
      { label: "View all National Teams", href: "/store" },
    ],
  },
  {
    title: "Shop by Type",
    links: [
      { label: "Kit out your Team", href: "/store" },
      { label: "Retro Football Shirts", href: "/store" },
      { label: "Football Boots", href: "/store" },
      { label: "Football T-Shirts", href: "/store" },
      { label: "Football Tracksuits", href: "/store" },
      { label: "Goalkeeper Shirts", href: "/store" },
      { label: "Kids Shirts", href: "/store" },
      { label: "Toffs Retro Shirts", href: "/store" },
    ],
  },
  {
    title: "Customer Services",
    links: [
      { label: "About Us", href: "#" },
      { label: "Sitemap", href: "#" },
      { label: "Categories Sitemap", href: "#" },
      { label: "Help / FAQs", href: "#" },
      { label: "Order Status", href: "#" },
      { label: "Returns", href: "#" },
      { label: "Shipping", href: "#" },
      { label: "Shirt Printing", href: "#" },
      { label: "Sell Shirts", href: "#" },
      { label: "Affiliates US", href: "#" },
      { label: "Affiliates UK", href: "#" },
      { label: "Ordering", href: "#" },
      { label: "Contact Us", href: "#" },
      { label: "Privacy Policy", href: "#" },
      { label: "Terms & Conditions", href: "#" },
      { label: "Exclusive Discounts", href: "#" },
    ],
  },
]

// ─── Social Links ───────────────────────────────────────────────────────────

export const SOCIAL_LINKS = [
  {
    label: "Facebook",
    href: "https://facebook.com/uksoccershop",
    icon: "facebook" as const,
  },
  {
    label: "X (formerly Twitter)",
    href: "https://x.com/uksoccershop",
    icon: "twitter" as const,
  },
]

// ─── Payment Icons ──────────────────────────────────────────────────────────

export const PAYMENT_ICONS: PaymentIcon[] = [
  { id: "visa", name: "Visa", image: "/images/payment-icon/Payment Method/Visa.png" },
  { id: "amex", name: "American Express", image: "/images/payment-icon/Payment Method/Amex.png" },
  { id: "discover", name: "Discover", image: "/images/payment-icon/Payment Method/Discover.png" },
  { id: "mastercard", name: "Mastercard", image: "/images/payment-icon/Payment Method/Mastercard.png" },
  { id: "paypal", name: "PayPal", image: "/images/payment-icon/Payment Method/PayPal.png" },
  { id: "gpay", name: "Google Pay", image: "/images/payment-icon/Payment Method/GooglePay.png" },
  { id: "applepay", name: "Apple Pay", image: "/images/payment-icon/Payment Method/ApplePay.png" },
]

// ─── Utility Nav ────────────────────────────────────────────────────────────

// TODO: Add Wish List back when wishlist feature is implemented
export const UTILITY_NAV = [
  { label: "Account", href: "/account" },
  { label: "Track My Order", href: "/account" },
]
